import {Inject, Injectable} from '@angular/core';

import {
  BooxAppService,
  BooxMessageService,
  BooxStorageService,
  BooxSessionService,
  ISessionModel,
  IStringAnyMap,
} from 'lib-boox';
import {HeroLocalizationService, IHeroLocale} from "./hero-localization.service";
import {HeroDictionaryService} from "./hero-dictionary.service";
import {NavigationEnd, Router} from "@angular/router";
import {HERO_APPLICATIONS} from "../token";
import {filter, map, tap} from "rxjs";

@Injectable({
  providedIn: 'root'
})
export class HeroHostService extends BooxAppService{

  constructor(
    message: BooxMessageService,
    storage: BooxStorageService,
    session: BooxSessionService,
    protected locale : HeroLocalizationService,
    protected dictionary : HeroDictionaryService,

    private router: Router,

    @Inject(HERO_APPLICATIONS) public apps: IStringAnyMap[],
  ) { super(message, storage, session); }

  /**
   * Updates the application status and sends a corresponding message.
   * This method prevents duplicate logic for status updates.
   *
   * @param status - The new application status
   */
  override setStatus(status: string): void {
    this.message.to(`host.status.${status}`, {}); // Send a status update message
    super.setStatus(status); // Update the state with the new status
  }

  public override boot(): void {
    super.boot();

    // inform the session has been updated to all app(s)
    this.session.$session.pipe().subscribe((session) => this.message.to('session.run.update', { session }));

    // subscribe to the locale observable and update the dictionary
    // and inform the app(s) of the locale change
    this.locale.locale$.pipe(
      filter((locale: IHeroLocale | null): locale is IHeroLocale => locale !== null),
      tap((locale : IHeroLocale) => this.message.to('locale.run.update', { locale })),
      map((locale: IHeroLocale) => locale.language.code),
      tap((code: string) => this.dictionary.load$(code, 'app-host').subscribe())
    ).subscribe();

    // on app status change
    this.message.on$('guest.status.*').pipe(
      map(({type}) => type.split('.').slice(-1)[0])
    ).subscribe((type) => this.update('guest.app.status', type));

    this.message.on$('guest.status.ready').subscribe(() => this.get('guest.app.auto_start') && this.guestAppStart());
    this.message.on$('app.status.ready').subscribe(() => this.get('guest.app.auto_start') && this.guestAppStart());
    this.message.on$('guest.status.waiting').subscribe(() => this.get('guest.app.auto_user') && this.guestAppUpdateUser());
    this.message.on$('session.run.signout').subscribe(() => this.guestAppReload());

    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map((event) => <NavigationEnd>event), // cast the event to NavigationEnd to make ts happy
      map((event : NavigationEnd) => {
        const app = this.findApp(event.url);
        return app;
      }),
      filter((app) => !!app), // filter out null values
      tap((app) => {
        this.useApp(app as IStringAnyMap);
      }) // use the app
    ).subscribe();

    // ****************** boot done -> ready ******************
    this.ready()
  }

  /**
   * Find the application object based on the route string
   * @param routeString The route string to match
   * @returns The application object if found, otherwise null
   */
  private findApp(routeString: string) {
    // Split the input route string into segments
    // const inputSegments = ['/',...routeString.split('/').filter(segment => segment)]; // Remove empty segments
    const inputSegments = [...routeString.split('/').filter(segment => segment)]; // Remove empty segments

    return this.apps.find(appObj => {
      const routeSegments = appObj['route'].filter((segment:string) => segment); // Remove empty segments from route array
      // Check if the routeSegments match the beginning of inputSegments
      return routeSegments.every((segment:string, index:number) => segment === inputSegments[index]);
    });
  }




  override ready() {
    this.locale.loadLocale();
    super.ready();
  }

  override start(): void {
    super.start();
    // auto start the default app
    const app = this.apps.find((app) => app['default']) || null;
    if(app) { this.useApp(app) }

    this.running();
  }



  public useApp(app: IStringAnyMap | null) {
    // inform the app to use the selected app
    // this will not effect future actions
    this.message.to('guest.app.use', { app }, 'host');
    // update the app in the state
    this.update('guest.app.current', app);
  }

  public guestAppReload() {
    this.message.to('app.run.reload', {}, 'host');
  }

  public guestAppStart() {
    this.message.to('app.run.start', {
      session: this.session.session,
      language: this.locale.getLocale()?.language.code,
      locale: this.locale.getLocale(),
    }, 'app');
    // legancy
    this.message.to('app.host.session.update', {
      session: this.session.session
    });
    this.message.to('app.host.user.update', {
      user : this.session.session
    })

  }

  public guestAppUpdateUser() {
    const session: ISessionModel | null = this.session?.session ?? null;
    const user: IStringAnyMap | null = session
      ? (({ name, first_name, last_name, email }) => ({ name, first_name, last_name, email }))(session)
      : null;
    this.message.to('user.run.update', { user }, 'app');
  }
}
